import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import { Stack, Box, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import { TypographyTitle } from '../style/typography'
import BarLine from '../components/bar_line';
import MapRiskMax from '../components/map_risk_rain';
import VerticalBar from '../components/vertical_bar';
import { H1_AREA_RISK_LEGEND } from '../const/legend';
import { RISK_LEVEL_POINT } from '../const/color';
import { GRID_RANK } from '../const/grid';
import { LANDSLIDE_Y_2000, LANDSLIDE_X_0_2000 } from '../const/annotations';
import { BASE_LAYER_LIST } from '../const/map_config';
import CommonCard from '../item/common_card';
import MaxRiskH1Txt from '../item/max_risk_h1_txt';
import Proggress from '../item/progress';
import HeaderSub from '../item/header_sub';
import { useDataFromS3 } from '../hooks/useDataFromS3';
import '../style/legend_risk.css';
import { setAreaRiskColor } from '../func/color';
import {
    getMaxRiskH1ByCode, getAllByCode, getMaxByDateTime, getTimeAry,
    getAllByDateTimeWithAreaInfo, getAllByDateTimeWithAreaInfoH1, getRankWithLimit, getH1RankWithLimit, convertAreaDataFromMshData
} from '../func/util';
import choropleth_area from '../data/area.json';
import choropleth_msh from '../data/msh.json';

const AreaRisk = (props) => {
    const [selectLayer, setSelectLayer] = useState(
        BASE_LAYER_LIST.find(layer => layer.index.toString() === props.defaultUnit) || BASE_LAYER_LIST[1]
    );

    // 市町村、字、メッシュ、個別の選択をクリアするかしないかのフラグ
    const [shouldClearSelect, setShouldClearSelect] = useState(false)
    // 最大土砂災害発生リスク
    const [landslideRiskMaxVal, setLandslideRiskMaxVal] = useState(null);
    // 最大1時間雨量（mm/h）
    const [landslideH1MaxVal, setLandslideH1MaxVal] = useState(null);
    // 地図（市町村・字・メッシュ）
    const [cityLandslideRiskH1, setCityLandslideRiskH1] = useState(null);
    const [areaLandslideRiskH1, setAreaLandslideRiskH1] = useState(null);
    const [mshLandslideRiskH1, setMshLandslideRiskH1] = useState(null);
    // 最大値(複合チャート（土砂災害発生リスク）)
    const [landslideMaxRiskDatetime, setLandslideMaxRiskDatetime] = useState(null);
    // 最大値(複合チャート（1時間雨量）)
    const [landslideMaxH1Datetime, setLandslideMaxH1Datetime] = useState(null);
    // ランキング
    const [landslideAllRankData, setLandslideAllRankData] = useState(null);
    const [h1AllRankData, setH1AllRankData] = useState(null);

    const [dateArray, setDateArray] = useState(null);
    const [shouldFetch, setShouldFetch] = useState(false);
    
    const location = useLocation();
    const prevDatetimeRef = useRef(null);

    // 全データ再取得の要否を定期監視する
    const { data: datetimeLatest, isLoading, error } = useDataFromS3(`api/get_datetime_latest`);

    // サーバーの最終更新日時が変更されているか判定
    useEffect(() => {
        if (datetimeLatest && JSON.stringify(datetimeLatest) !== JSON.stringify(prevDatetimeRef.current)) {
            prevDatetimeRef.current = datetimeLatest;
            setShouldFetch(true); // datetimeLatestが変わったらメインデータを取得
        } else {
            setShouldFetch(false); // datetimeLatestが変わってないならメインデータを取得しない
        }
    }, [datetimeLatest]);

    let landslide_msh_url = null
    if (shouldFetch) {
        landslide_msh_url = `api/get_risk`
    }

    // reload page when change pathname
    useEffect(() => {
        prevDatetimeRef.current = null
    }, [location.pathname])

    // 土砂災害リスクを取得
    const {
        data: mshLandslideData,
        loading: mshLandslideLoading,
        error: mshLandslideError,
    } = useDataFromS3(landslide_msh_url);

    // 複合グラフのマーカー情報
    const getPoints = (date_ary, data_x) => {
        let points = []
        for (let i = 0; i < data_x.length; i++) {
            const color = setAreaRiskColor(RISK_LEVEL_POINT, data_x[i])
            const point = {
                x: date_ary[i],
                y: data_x[i],
                marker: {
                    size: 4,
                    fillColor: color,
                    strokeColors: 'transparent',
                    strokeWidth: 0,
                    shape: "circle"
                }
            }
            points.push(point)
        }
        return points
    }

    // 複合グラフの数値表示変換処理
    const format = (val) => parseFloat(val).toFixed(0);

    // ランキングのカラー（期間内　土砂災害発生リスク）
    const getRankData = (data, isOver, sliderValueRef, getLabelValue, getYValue) => {
        let rankData = data;
        if (isOver) {
            // 取得されたリスクの最大値を並べ替え（昇順/低い順）
            rankData.sort((a, b) => {
                if (a.max === b.max) {
                    return a.area_code - b.area_code;
                }
                return a.max - b.max
            })
        } else {
            // 取得されたリスクの最大値を並べ替え（降順/高い順）
            rankData.sort((a, b) => b.max - a.max)
            rankData.sort((a, b) => {
                if (a.max === b.max) {
                    return b.area_code - a.area_code;
                }
                return b.max - a.max
            })
        }

        // sliderValueに応じてリスクの最大値を並べ替え
        let series_ary = []
        let category_ary = []
        if (isOver) {
            for (let i = sliderValueRef.current[1]; i > sliderValueRef.current[0] - 1; i--) {
                if (rankData.length > i) {
                    let str_time = ''
                    let time_index = 0
                    if (rankData[i].index_Val) {
                        str_time = `　${dateArray[rankData[i].index_Val]}`
                        time_index = rankData[i].index_Val
                    }
                    series_ary.push({
                        x: '',
                        y: rankData[i].max,
                        custom: {
                            area_name: rankData[i].area_name,
                            area_code: rankData[i].area_code,
                            area_lng: rankData[i].area_lng,
                            area_lat: rankData[i].area_lat,
                            str_time: str_time,
                            time_index: time_index
                        }
                    })
                    category_ary.push(rankData[i].area_name)
                }
            }
        } else {
            rankData.map(item => {
                let str_time = ''
                let time_index = 0
                if (item.index_Val) {
                    str_time = `　(${dateArray[item.index_Val]})`
                    time_index = item.index_Val

                }
                series_ary.push({
                    x: '',
                    y: item.max,
                    custom: {
                        area_name: item.area_name,
                        area_code: item.area_code,
                        area_lng: item.area_lng,
                        area_lat: item.area_lat,
                        str_time: str_time,
                        time_index: time_index
                    }
                })
                category_ary.push(item.area_name)
            })
        }
        return { rankData, series_ary, category_ary };
    }

    // ランキングクリック時の動作
    const verticalBarClickEvent = (clickedItem) => {
        props.setBsnMapCenter([clickedItem.custom.area_lng, clickedItem.custom.area_lat])
        props.setRiskMapCenter([clickedItem.custom.area_lng, clickedItem.custom.area_lat])
        setShouldClearSelect(false)
        setSelectLayer(BASE_LAYER_LIST[1])
        props.setSelectArea([clickedItem.custom.area_code])
        props.setSelectSubCity([]);
        props.setSelectMsh([]);
    }

    // ランキングのカラー（期間内　土砂災害発生リスク）
    const getRiskRankColor = (value, series, seriesIndex, dataPointIndex, w) => {
        return setAreaRiskColor(RISK_LEVEL_POINT, value)
    }

    // ランキングのカラー（期間内　1時間雨量）
    const getRainRankColor = (value, series, seriesIndex, dataPointIndex, w) => {
        return "#5c9fed";
    }

    // ランキングのフォーマット
    const getFormat = (val, opts) => {
        const customData = opts.w.config.series[0].data[opts.dataPointIndex].custom;
        return `${val} ${customData.str_time}`;
    }

    // ランキングのラベルフォーマット
    const getLabelFormatString = (value) => {
        let str = String(value);
        // 8文字以上は省略
        if (str.length >= 8) {
            return str = str.slice(0, 3) + '…' + str.slice(-4);
        }
        return '　'.repeat(8 - str.length) + str;
    }

    // ランキングのX軸ツールチップフォーマット
    const tooltipFormatX = (opts) => {
        return opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex].custom.area_name;
    }

    // ランキングのY軸ツールチップフォーマット
    const tooltipFormatY = (val) => {
        return String(val);
    }

    // ランキングのY軸ツールチップフォーマット
    const tooltipFormatCustom = (opts, areaNameAry, areaRiverNameAry) => {
        const label = opts.w.globals.labels[opts.dataPointIndex]; // y軸ラベル
        const value = opts.series[opts.seriesIndex][opts.dataPointIndex]; // 値
        const seriesName = opts.w.globals.seriesNames[opts.seriesIndex];

        return `<div style="padding: 4px 8px; font-size: 12px;">
                    ${label} ${seriesName}: ${value}
                </div>`;
    }

    // データ整備用のパラメータを取得
    const getParams = (keyAry, keyName = '') => {
        const features_area = choropleth_area.features
        const features_msh = choropleth_msh.features

        // メッシュデータ取得
        const keySet = new Set(keyAry.map(String));
        const masterMshFeatures = features_msh.filter(f =>
            keySet.has(String(f.properties[keyName]))
        );

        // メッシュデータの properties.area_code を配列で取得(これが対象の字コード)
        const area_code_ary = [...new Set(masterMshFeatures.map(feature => feature.properties.area_code))];
        // 字のマスターデータ取得
        const masterAreaFeatures = features_area.filter(f =>
            area_code_ary.includes(f.properties.id)
        );
        // メッシュデータの properties.id を配列で取得(これが対象のメッシュコード)
        const msh_code_ary = masterMshFeatures.map(feature => feature.properties.id);
        return {
            msh_code_ary, area_code_ary,
            masterMshFeatures, masterAreaFeatures
        };
    }

    useEffect(() => {
        // ローディング開始
        props.setReady(prev => {
            const updated = Object.fromEntries(
                Object.keys(prev).map(key => [key, false])
            );
            return updated;
        });

        const fetchData = async () => {
            let params_obj = {}
            let msh_landslide_risk_h1_all_datetime = []
            let area_landslide_risk_h1_all_datetime = []
            let city_landslide_risk_h1_max = []
            let area_landslide_risk_h1_max = []
            let msh_landslide_risk_h1_max = []
            let landslide_risk_rank = []
            let landslide_h1_rank = []
            let landslide_risk_maxVal = null
            let landslide_h1_maxVal = null
            let date_ary = []
            let landslide_risk_max_datetime = []
            let landslide_h1_max_datetime = []
            let landslide_risk_all_datetime = []
            let landslide_h1_all_datetime = []

            if (props.selectBsn && Array.isArray(props.selectBsn)
                && props.selectBsn.length > 0) {
                params_obj = getParams(props.selectBsn, '')
            } else if (props.selectArea && Array.isArray(props.selectArea)
                && props.selectArea.length > 0) {
                params_obj = getParams(props.selectArea, 'area_code')
            } else if (props.selectMsh && Array.isArray(props.selectMsh)
                && props.selectMsh.length > 0) {
                params_obj = getParams(props.selectMsh, 'id')
            } else if (props.selectSubCity && Array.isArray(props.selectSubCity)
                && props.selectSubCity.length > 0) {
                params_obj = getParams(props.selectSubCity, 'city_code')
            } else {
                params_obj = getParams(props.selectCitys, 'city_code')
            }

            if (Object.keys(params_obj).length > 0) {
                // 日時の配列を取得
                date_ary = props.h6Switch ? getTimeAry(datetimeLatest, props.h6Switch) : datetimeLatest

                //// メッシュコードごとのリスクと雨量のオブジェクト配列を取得
                // [{ code: 503021111（メッシュコード）, 
                // risk_val: [3, 2, 3, 2, 3, 4, 8, 10, 14, 24], 
                // h1_val: [1, 1, 1, 1, 1, 1, 9, 5, 6, 7],}, ..............]
                msh_landslide_risk_h1_all_datetime = getAllByCode(params_obj.msh_code_ary, mshLandslideData, props.h6Switch)
                if (msh_landslide_risk_h1_all_datetime.length > 0) {
                    //// 字コードごとのリスクと雨量のオブジェクト配列を取得
                    // [{ code: 401010010（字コード）, 
                    // risk_val: [3, 2, 3, 2, 3, 4, 8, 10, 14, 24], 
                    // h1_val: [1, 1, 1, 1, 1, 1, 9, 5, 6, 7],}, ..............]
                    area_landslide_risk_h1_all_datetime = convertAreaDataFromMshData(params_obj.masterMshFeatures, msh_landslide_risk_h1_all_datetime)
                    // 地図の字レイヤー用のデータ
                    area_landslide_risk_h1_max = getMaxRiskH1ByCode(area_landslide_risk_h1_all_datetime, 0)
                    // 地図の市町村レイヤー用のデータ
                    city_landslide_risk_h1_max = getMaxRiskH1ByCode(area_landslide_risk_h1_all_datetime, 5)
                    // 地図のメッシュレイヤー用のデータ
                    msh_landslide_risk_h1_max = getMaxRiskH1ByCode(msh_landslide_risk_h1_all_datetime, 0)

                    // 土砂災害発生リスクランキング用のデータ
                    landslide_risk_all_datetime = getAllByDateTimeWithAreaInfo(params_obj.masterAreaFeatures, area_landslide_risk_h1_all_datetime)
                    landslide_risk_rank = getRankWithLimit(landslide_risk_all_datetime, 200)
                    // 1時間雨量ランキング用のデータ
                    landslide_h1_all_datetime = getAllByDateTimeWithAreaInfoH1(params_obj.masterAreaFeatures, area_landslide_risk_h1_all_datetime)
                    landslide_h1_rank = getH1RankWithLimit(landslide_h1_all_datetime, 200)
                    // 最大土砂災害発生リスク用のデータ
                    landslide_risk_maxVal = landslide_risk_rank[0].max
                    // 土砂の複合グラフのラインチャート用のデータ
                    landslide_risk_max_datetime = getMaxByDateTime(area_landslide_risk_h1_all_datetime, 'rsk_dc_value_num');
                    // 複合グラフのbarチャート用のデータ
                    landslide_h1_max_datetime = getMaxByDateTime(area_landslide_risk_h1_all_datetime, 'h1_dc_value_num');
                    // 最大1時間雨量用のデータ
                    landslide_h1_maxVal = Math.max(...landslide_h1_max_datetime.slice(6));
                }
            }

            // 地図（土砂災害発生リスク）
            setCityLandslideRiskH1(city_landslide_risk_h1_max.length > 0 ? city_landslide_risk_h1_max : null)
            setAreaLandslideRiskH1(area_landslide_risk_h1_max.length > 0 ? area_landslide_risk_h1_max : null)
            setMshLandslideRiskH1(msh_landslide_risk_h1_max.length > 0 ? msh_landslide_risk_h1_max : null)
            // 最大土砂災害発生リスク
            setLandslideRiskMaxVal(landslide_risk_maxVal)
            // 最大1時間雨量（mm/h）
            setLandslideH1MaxVal(landslide_h1_maxVal)
            // 複合チャート（土砂災害発生リスク）
            setLandslideMaxRiskDatetime(landslide_risk_max_datetime.length > 0 ? landslide_risk_max_datetime : null)
            // 複合チャート（1時間雨量）
            setLandslideMaxH1Datetime(landslide_h1_max_datetime.length > 0 ? landslide_h1_max_datetime : null)
            // ランキング（期間内　土砂災害発生リスク）
            setLandslideAllRankData(landslide_risk_rank.length > 0 ? landslide_risk_rank : null)
            // ランキング（期間内　1時間雨量）
            setH1AllRankData(landslide_h1_rank.length > 0 ? landslide_h1_rank : null)

            // 日時
            setDateArray(date_ary.length > 0 ? date_ary : null)
        };
        if (datetimeLatest && mshLandslideData) {
            fetchData();
        }
    }, [datetimeLatest,
        mshLandslideData,
        props.selectCitys, props.selectSubCity, props.selectArea, props.selectMsh,
        props.selectTelCode, props.selectBsn, props.h6Switch
    ]);

    if (isLoading || datetimeLatest === undefined
        || (error && error.message.includes("Received HTML"))
        || mshLandslideLoading || mshLandslideData === undefined
        || (mshLandslideError && mshLandslideError.message.includes("Received HTML"))
    ) {
        // Loading表示
        return <Proggress />
    }

    if (error || mshLandslideError) {
        return (
            <Typography
                sx={{
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    color: 'white',
                }}
            >
                申し訳ございません、データ取得に失敗しました。 <br /> 恐れ入りますが、しばらく時間をおいてから再度お試しください。
            </Typography>
        )
    }

    return (
        <>
            <Box
                sx={{
                    // グローバルヘッダーの「36px」と時刻表示分の「17px」、大枠のGridのマージン上下「8px」ずつを差し引きく
                    height: { xs: '100%', sm: 'calc(100dvh - 36px - 17px - 8px - 8px)' }, // 親が高さを持つ
                    boxSizing: 'border-box',
                }}
            >
                <HeaderSub
                    pageTitle={""}
                    dateTime={datetimeLatest && datetimeLatest[12]}
                />
                <Grid container
                    spacing={0}
                    columns={12}
                    sx={{ height: '100%', width: '100%', padding: 0, m: 0 }} // Grid全体の高さ100%
                >
                    <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                        <Stack spacing={0} >
                            {/* 最大土砂災害発生リスク・最大1時間雨量(mm/h) */}
                            <MaxRiskH1Txt
                                titleRisk={"最大土砂災害発生リスク"}
                                titleH1={"最大1時間雨量　(mm/h)"}
                                riskMaxVal={landslideRiskMaxVal}
                                h1MaxVal={landslideH1MaxVal}
                                setColor={setAreaRiskColor}
                                levelPoint={RISK_LEVEL_POINT}
                                riskVal={(riskVal, riskFontColor) => (
                                    <TypographyTitle sx={{ color: riskFontColor }}>
                                        {`最大リスク値：${riskVal}`}
                                    </TypographyTitle>
                                )}
                                height={'calc(13dvh - (36px * 0.13) - (17px * 0.13) - (8px * 0.13) - (8px * 0.13) - 31px - 5px)'}
                            />
                            {/* 土砂災害発生リスク・雨量 */}
                            <CommonCard
                                child={
                                    <MapRiskMax
                                        selectSubCity={props.selectSubCity}
                                        setSelectSubCity={props.setSelectSubCity}
                                        selectArea={props.selectArea}
                                        setSelectArea={props.setSelectArea}
                                        selectMsh={props.selectMsh}
                                        setSelectMsh={props.setSelectMsh}
                                        selectBsn={props.selectBsn}
                                        setSelectBsn={props.setSelectBsn}
                                        selectCitys={props.selectCitys}
                                        cityLandslideRiskH1={cityLandslideRiskH1}
                                        areaLandslideRiskH1={areaLandslideRiskH1}
                                        mshLandslideRiskH1={mshLandslideRiskH1}
                                        riskMapZoom={props.riskMapZoom}
                                        riskMapCenter={props.riskMapCenter}
                                        selectLayer={selectLayer}
                                        setSelectLayer={setSelectLayer}
                                        setRiskColor={(risk) => setAreaRiskColor(RISK_LEVEL_POINT, risk)}
                                        shouldClearSelect={shouldClearSelect}
                                        setShouldClearSelect={setShouldClearSelect}
                                        tooltipLabel={"土砂災害発生リスク"}
                                        legendLabel={"土砂災害発生リスク"}
                                    />
                                }
                                title={"土砂災害発生リスク"}
                                xsHeight="300px"
                                height='calc(87dvh - (36px * 0.87) - (17px * 0.87) - (8px * 0.87) - (8px * 0.87) - 31px - 5px)'
                            />
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 7 }}>
                        <Stack spacing={0} >
                            {/* 期間内1時間雨量、土砂災害発生リスク */}
                            <CommonCard
                                child={
                                    <>
                                        {H1_AREA_RISK_LEGEND()}
                                        <BarLine
                                            annotations={LANDSLIDE_Y_2000}
                                            limitVal={2000}
                                            dateArray={dateArray}
                                            xDatetime={landslideMaxRiskDatetime}
                                            yDatetime={landslideMaxH1Datetime}
                                            getPoints={getPoints}
                                            seriesNameX={'避難推奨レベル'}
                                            seriesNameY={'1時間雨量'}
                                            colors={['#5c9fed', '#5c9fed']}
                                            strokeColors={['#5c9fed', 'transparent']}
                                            shared={false}
                                            intersect={true}
                                            format={format}
                                            bottom={10}
                                            isReadyKey={['bar_line_soil']}
                                            isReadyFlg={props.isReady.bar_line_soil}
                                            setReady={props.setReady}
                                            chooseNum={6}
                                        />
                                    </>
                                }
                                title={"期間内1時間雨量、土砂災害発生リスク"}
                                xsHeight="200px"
                                height='calc(30dvh - (36px * 0.3) - (17px * 0.3) - (8px * 0.3) - (8px * 0.3) - 31px - 5px)'
                            />
                            <Grid container spacing={1}>
                                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                                    {/* 期間内　土砂災害発生リスクランキング */}
                                    <CommonCard
                                        child={
                                            <>
                                                <VerticalBar
                                                    distance={20}
                                                    limitVal={2000}
                                                    annotations={LANDSLIDE_X_0_2000}
                                                    grid={GRID_RANK}
                                                    allRankData={landslideAllRankData}
                                                    dateArray={dateArray}
                                                    getRankData={getRankData}
                                                    plusValue={10}
                                                    verticalBarClickEvent={verticalBarClickEvent}
                                                    getRankColor={getRiskRankColor}
                                                    fontSize={'12px'}
                                                    getFormat={getFormat}
                                                    maxWidth={160}
                                                    getLabelFormat={getLabelFormatString}
                                                    enabled={true}
                                                    tooltipFormatX={tooltipFormatX}
                                                    tooltipFormatY={tooltipFormatY}
                                                    tooltipFormatCustom={tooltipFormatCustom}
                                                    height={'100%'}
                                                    isReadyKey={['rank_soil']}
                                                    isReadyFlg={props.isReady.rank_soil}
                                                    setReady={props.setReady}
                                                />
                                            </>
                                        }
                                        title={"期間内　土砂災害発生リスクランキング"}
                                        xsHeight="200px"
                                        height='calc(70dvh - (36px * 0.7) - (17px * 0.7) - (8px * 0.7) - (8px * 0.7) - 31px - 5px)'
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                                    {/* 期間内　1時間雨量ランキング */}
                                    <CommonCard
                                        child={
                                            <>
                                                <VerticalBar
                                                    distance={20}
                                                    limitVal={100}
                                                    grid={GRID_RANK}
                                                    allRankData={h1AllRankData}
                                                    dateArray={dateArray}
                                                    getRankData={getRankData}
                                                    plusValue={10}
                                                    verticalBarClickEvent={verticalBarClickEvent}
                                                    getRankColor={getRainRankColor}
                                                    fontSize={'12px'}
                                                    getFormat={getFormat}
                                                    maxWidth={160}
                                                    getLabelFormat={getLabelFormatString}
                                                    enabled={true}
                                                    tooltipFormatX={tooltipFormatX}
                                                    tooltipFormatY={tooltipFormatY}
                                                    tooltipFormatCustom={tooltipFormatCustom}
                                                    height={'100%'}
                                                    isReadyKey={['rank_rain']}
                                                    isReadyFlg={props.isReady.rank_rain}
                                                    setReady={props.setReady}
                                                />
                                            </>
                                        }
                                        title={"期間内　1時間雨量ランキング"}
                                        xsHeight="200px"
                                        height='calc(70dvh - (36px * 0.7) - (17px * 0.7) - (8px * 0.7) - (8px * 0.7) - 31px - 5px)'
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </Grid>
                </Grid>
            </Box >
        </>
    )
}
export default AreaRisk;